import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { Clock, Calendar, TrendingUp, BarChart3 } from 'lucide-react';
import api from '../services/api';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';

const HeuresPage = () => {
  const { user, isCoordinatrice } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('semaine');
  const [heures, setHeures] = useState(null);
  const [recapCoord, setRecapCoord] = useState(null);

  useEffect(() => {
    loadHeures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode]);

  const loadHeures = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let debut, fin;

      if (periode === 'semaine') {
        debut = startOfWeek(now, { weekStartsOn: 1 });
        fin = endOfWeek(now, { weekStartsOn: 1 });
      } else {
        debut = startOfMonth(now);
        fin = endOfMonth(now);
      }

      const debutStr = format(debut, 'yyyy-MM-dd');
      const finStr = format(fin, 'yyyy-MM-dd');

      if (isCoordinatrice) {
        const response = await api.get(`/heures/recap?debut=${debutStr}&fin=${finStr}`);
        console.log('Recap coordinatrice:', response.data);
        setRecapCoord(response.data);
      } else {
        const response = await api.get(`/heures/auxiliaire/${user.id}?debut=${debutStr}&fin=${finStr}`);
        console.log('Heures auxiliaire:', response.data);
        setHeures(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement heures:', error);
      if (isCoordinatrice) {
        setRecapCoord({ auxiliaires: [] });
      } else {
        setHeures({
          heuresSemaine: 0,
          heuresWeekend: 0,
          total: 0,
          details: []
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="pb-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen">
      {/* Header moderne avec gradient */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white p-6 rounded-b-[2rem] shadow-xl sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-1 drop-shadow-sm">⏰ Suivi des heures</h1>
            <p className="text-blue-100 text-sm font-medium">
              {periode === 'semaine' ? 'Semaine en cours' : 'Mois en cours'}
            </p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
            <BarChart3 size={28} className="text-white" />
          </div>
        </div>
      </div>

      <div className="p-4 space-y-5">
        {/* Sélecteur période - Design moderne */}
        <div className="flex gap-3">
          <button
            onClick={() => setPeriode('semaine')}
            className={`flex-1 py-3 rounded-2xl font-bold transition-all duration-200 ${
              periode === 'semaine'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300'
            }`}
          >
            📅 Semaine
          </button>
          <button
            onClick={() => setPeriode('mois')}
            className={`flex-1 py-3 rounded-2xl font-bold transition-all duration-200 ${
              periode === 'mois'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300'
            }`}
          >
            📆 Mois
          </button>
        </div>

        {/* Vue auxiliaire */}
        {!isCoordinatrice && heures && (
          <>
            {/* Résumé des heures - Design cards premium */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg p-4 text-white">
                <div className="bg-white/20 rounded-full w-10 h-10 flex items-center justify-center mb-2 mx-auto">
                  <Clock size={20} className="text-white" />
                </div>
                <p className="text-2xl font-bold text-center">
                  {heures.heuresSemaine || 0}h
                </p>
                <p className="text-xs text-blue-100 text-center mt-1 font-medium">Semaine</p>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-lg p-4 text-white">
                <div className="bg-white/20 rounded-full w-10 h-10 flex items-center justify-center mb-2 mx-auto">
                  <Calendar size={20} className="text-white" />
                </div>
                <p className="text-2xl font-bold text-center">
                  {heures.heuresWeekend || 0}h
                </p>
                <p className="text-xs text-orange-100 text-center mt-1 font-medium">Week-end</p>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg p-4 text-white">
                <div className="bg-white/20 rounded-full w-10 h-10 flex items-center justify-center mb-2 mx-auto">
                  <TrendingUp size={20} className="text-white" />
                </div>
                <p className="text-2xl font-bold text-center">
                  {heures.total || 0}h
                </p>
                <p className="text-xs text-green-100 text-center mt-1 font-medium">Total</p>
              </div>
            </div>

            {/* Détails par jour */}
            {heures.details && heures.details.length > 0 ? (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl p-2">
                    <Calendar size={20} className="text-white" />
                  </div>
                  <h3 className="font-bold text-gray-800">Détail par jour</h3>
                </div>
                <div className="space-y-3">
                  {heures.details.map((jour, index) => (
                    <div 
                      key={jour.date || index} 
                      className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <p className="font-bold text-gray-800">
                            {format(new Date(jour.date), 'EEEE d MMM', { locale: fr })}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            📋 {jour.interventions?.length || 0} intervention(s)
                          </p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold text-lg px-4 py-2 rounded-xl shadow-md">
                          {jour.totalJour || 0}h
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 text-center">
                <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Calendar size={40} className="text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">Aucune intervention</p>
                <p className="text-sm text-gray-400 mt-1">sur cette période</p>
              </div>
            )}
          </>
        )}

        {/* Vue coordinatrice */}
        {isCoordinatrice && recapCoord && (
          <>
            {recapCoord.auxiliaires && recapCoord.auxiliaires.length > 0 ? (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl p-2">
                    <BarChart3 size={20} className="text-white" />
                  </div>
                  <h3 className="font-bold text-gray-800">Récapitulatif par auxiliaire</h3>
                </div>
                <div className="space-y-4">
                  {recapCoord.auxiliaires.map((aux) => (
                    <div 
                      key={aux.auxiliaireId || aux.username} 
                      className="bg-gradient-to-r from-white to-blue-50 border-2 border-gray-200 rounded-2xl p-4 hover:shadow-lg transition-all"
                    >
                      <p className="font-bold text-gray-800 mb-3 text-lg">
                        👤 {aux.username || `${aux.prenom || ''} ${aux.nom || ''}`.trim()}
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-blue-100">
                          <p className="text-xl font-bold text-blue-600">
                            {aux.heuresSemaine || 0}h
                          </p>
                          <p className="text-xs text-gray-600 mt-1 font-medium">Semaine</p>
                        </div>
                        <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-orange-100">
                          <p className="text-xl font-bold text-orange-600">
                            {aux.heuresWeekend || 0}h
                          </p>
                          <p className="text-xs text-gray-600 mt-1 font-medium">Week-end</p>
                        </div>
                        <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-green-100">
                          <p className="text-xl font-bold text-green-600">
                            {aux.total || 0}h
                          </p>
                          <p className="text-xs text-gray-600 mt-1 font-medium">Total</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 text-center">
                <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Clock size={40} className="text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">Aucune heure enregistrée</p>
                <p className="text-sm text-gray-400 mt-1">sur cette période</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HeuresPage;
